import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from '../../../components/snackbar';
import { Upload } from '../../../components/upload';
import { ProductService } from '../../../api/product.service';

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export default function ProductImportDialog({ open, onClose, onSuccess }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleDrop = (acceptedFiles: File[]) => {
    const newFile = acceptedFiles[0];
    if (newFile) {
      setFile(newFile);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);
      const service = new ProductService();
      await service.importProducts(file);

      enqueueSnackbar('Products imported successfully', { variant: 'success' });
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(error?.message || 'Failed to import products', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Import Products</DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Upload a CSV or Excel file to import products.
            The file should contain columns: Name, Code, SKU, Type, Purchase Price, Sale Price, Current Stock, Minimum Stock.
          </Typography>

          <Upload
            file={file}
            onDrop={handleDrop}
            onDelete={() => setFile(null)}
            accept={{
              'text/csv': ['.csv'],
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
              'application/vnd.ms-excel': ['.xls'],
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          onClick={handleUpload}
          variant="contained"
          loading={uploading}
          disabled={!file}
        >
          Import
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
